import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Cropper from "react-easy-crop"; 
import { loadFont, toBase64, getTextPreviewStyle } from "../utils/appHelper";
import Loader from "../common/Loader";
import NotFound from "../common/NotFound";

// --- REDUX ACTIONS ---
import {
  getTemplateByIdRequest,
  getMusicsRequest,
  getFontsRequest,
  generateVideoRequest,
  generateVideoDataClear,
  updateCreatedInvitationInfoRequest,
  getTemplateByIdSuccess,
} from "../store/slice/appSlice";

import {
  setSlides,
  setSelectedSlide,
  setSelectedLayer,
  setTemplateConfig,
  updateSlideText,
  addSlideImage,
  removeSlideLayer // Import the delete action
} from "../store/slice/globalAppSlice";

import { GENERATE_VIDEO_STREAM } from "../config/endpoints";

/* =========================
   CONSTANTS
========================= */
const DESIGN_WIDTH = 1080;
const DESIGN_HEIGHT = 1920;
const PREVIEW_WIDTH = 360;
const PREVIEW_HEIGHT = 640;

/* =========================
   CROP HELPER UTILS
========================= */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) return null;

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvas.toDataURL("image/png");
};

/* =========================
   MAIN COMPONENT
========================= */
export default function SS2Editor() {
  const dispatch = useDispatch();
  const { slug } = useParams();
  const templateSlug = slug || 4;

  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const settingsRef = useRef(null);
  const fileInputRef = useRef(null);
  const loadedFontsRef = useRef(new Set());
  const previewContainerRef = useRef(null);

  const { 
    loginData, 
    user
  } = useSelector((state) => state.auth);

  const { 
    getTemplateByIdLoading, 
    getTemplateByIdError, 
    getMusicsData, 
    getFontsData,
    generateVideoData,
    generateVideoLoading,
    updateCreatedInvitationInfoLoading,
    updateCreatedInvitationInfoData,
    updateCreatedInvitationInfoError,
  } = useSelector((state) => state.app);

  const { 
    slides, 
    selectedSlide, 
    selectedLayer,
    templateConfig 
  } = useSelector((state) => state.global);
  
  const [activeTab, setActiveTab] = useState("style");
  const [generationStatus, setGenerationStatus] = useState("");
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [playingMusicId, setPlayingMusicId] = useState(null);
  const [selectedMusicCategory, setSelectedMusicCategory] = useState(null);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [tempImage, setTempImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropAspect, setCropAspect] = useState(null);
  const [previewWidth, setPreviewWidth] = useState(PREVIEW_WIDTH);
  const [saveJsonOnly, setSaveJsonOnly] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [musicError, setMusicError] = useState(false);
  const [musicModalOpen, setMusicModalOpen] = useState(false);
  const [settingsLayerKey, setSettingsLayerKey] = useState(null);

  const musicCategories = useMemo(() => getMusicsData || [], [getMusicsData]);

  useEffect(() => {
    if (musicCategories.length && !selectedMusicCategory) {
      setSelectedMusicCategory(musicCategories[0].category_id);
    }
  }, [musicCategories]);
  const fontList = useMemo(() => getFontsData || [], [getFontsData]);
  
  const isSuperAdmin = user?.role_id === 1;
  const slide = slides[selectedSlide];
  console.log({slide, getTemplateByIdError, selectedSlide, slides});


  useEffect(() => {
    dispatch(getTemplateByIdRequest({ slug: templateSlug }));
    dispatch(getMusicsRequest());
    dispatch(getFontsRequest());
  }, [dispatch, templateSlug]);

  // useEffect(() => {
  //   if (!fontList.length) return;
  //   fontList.forEach((font) => {
  //     if (!loadedFontsRef.current.has(font.family)) {
  //       loadFont(font);
  //       loadedFontsRef.current.add(font.family);
  //     }
  //   });
  // }, [fontList]);
  
  useEffect(() => {
    if (!slide) return;

    const loadSlideFonts = async () => {
      for (const t of slide.texts) {
        const font = fontList.find(
          (f) => f.family === t.font_track?.family
        );
        if (font) {
          await loadFont(font);
        }
      }
    };

    loadSlideFonts();

  }, [slide, fontList]);


  useEffect(() => {
    if (!slide) return;

    const updateWidth = () => {
      if (!previewContainerRef.current) return;

      const width = previewContainerRef.current.offsetWidth;

      setPreviewWidth(prev => {
        if (width > 0 && prev !== width) {
          return width;
        }
        return prev;
      });
    };

    updateWidth();

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [slide]);


  const scale = previewWidth ? previewWidth / DESIGN_WIDTH : PREVIEW_WIDTH / DESIGN_WIDTH;

  const selectLayerOnly = (type, index) => {
    if (selectedLayer?.type !== type || selectedLayer?.index !== index) {
        dispatch(setSelectedLayer({ type, index }));
        setSettingsLayerKey(null); // clear settings when switching layers via input focus
    }
  };

  const toggleLayerSettings = (type, index) => {
    if (selectedLayer?.type === type && selectedLayer?.index === index) {
        dispatch(setSelectedLayer(null));
        setShowFontPicker(false);
        setSettingsLayerKey(null);
    } else {
        dispatch(setSelectedLayer({ type, index }));
        setActiveTab("style");
        setSettingsLayerKey(`${type}-${index}`);
    }
  };

  const getActiveElement = () => {
    if (!selectedLayer) return null;
    if (selectedLayer.type === "text") return slides[selectedSlide]?.texts?.[selectedLayer.index];
    if (selectedLayer.type === "image") return slides[selectedSlide]?.images?.[selectedLayer.index];
    return null;
  };

  const activeElement = getActiveElement();

  /* =========================
     DELETE LAYER
  ========================= */
  const handleDeleteLayer = () => {
    if (!selectedLayer) return;
    const confirmDelete = window.confirm("Are you sure you want to remove this item?");
    if (confirmDelete) {
        dispatch(removeSlideLayer({
            slideIndex: selectedSlide,
            layerIndex: selectedLayer.index,
            type: selectedLayer.type
        }));
    }
  };

  const handleCopyTextLayer = () => {
    if (!selectedLayer || selectedLayer.type !== 'text') return;
    const newSlides = JSON.parse(JSON.stringify(slides));
    const original = newSlides[selectedSlide].texts[selectedLayer.index];
    const copied = { ...JSON.parse(JSON.stringify(original)), content: "Add Your Text" };
    newSlides[selectedSlide].texts.push(copied);
    dispatch(setSlides(newSlides));
    const newIndex = newSlides[selectedSlide].texts.length - 1;
    dispatch(setSelectedLayer({ type: "text", index: newIndex }));
    setSettingsLayerKey(`text-${newIndex}`);
  };

  /* =========================
     ACTIONS
  ========================= */
  const handleTextChange = (value) => {
    if (selectedLayer?.type === 'text') {
      dispatch(updateSlideText({ 
        slideIndex: selectedSlide, 
        textIndex: selectedLayer.index, 
        content: value 
      }));
    }
  };

  const updateLayerProperty = (key, value, nestedKey = null) => {
    const newSlides = JSON.parse(JSON.stringify(slides));
    const target = selectedLayer.type === "text" 
      ? newSlides[selectedSlide].texts[selectedLayer.index]
      : newSlides[selectedSlide].images[selectedLayer.index];

    // if (nestedKey) target[key][nestedKey] = value;
    // else target[key] = value;

    if (nestedKey) {
      // Ensure nested object exists before setting
      if (!target[key] || typeof target[key] !== 'object') {
        target[key] = {};
      }
      target[key][nestedKey] = value;
    } else {
      target[key] = value;
    }
    
    dispatch(setSlides(newSlides));
  };

  const handleFontFamilyChange = async (fontFamily) => {
    if (!selectedLayer) return;

    const selectedFont = fontList.find((f) => f.family === fontFamily);
    if (!selectedFont) return;

    // 🔥 Load font first
    if (!loadedFontsRef.current.has(selectedFont.family)) {
      await loadFont(selectedFont);
      loadedFontsRef.current.add(selectedFont.family);
    }

    const newSlides = JSON.parse(JSON.stringify(slides));
    const t = newSlides[selectedSlide].texts[selectedLayer.index];

    t.font_track = {
      ...t.font_track,
      id: selectedFont.id,
      family: selectedFont.family,
      fallback: selectedFont.fallback || "serif",
      folder: selectedFont.folder || "",
      filename: selectedFont.filename || "",
      files: selectedFont.files,
    };

    // 🔥 Force re-render
    dispatch(setSlides([...newSlides]));
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const imageDataUrl = await toBase64(file);
      setTempImage(imageDataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropAspect(null);
      setCropModalOpen(true);
      e.target.value = null;
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleUseFullImage = async () => {
    try {
      const img = await createImage(tempImage);
      const full = { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };
      const croppedImage = await getCroppedImg(tempImage, full);
      const newImage = {
        base64: croppedImage,
        position: { x: 300, y: 600 },
        width: 400,
        height: 400,
        borderRadius: 0,
        start: 0,
        end: slide.duration || 6,
      };
      dispatch(addSlideImage({ slideIndex: selectedSlide, image: newImage }));
      setCropModalOpen(false);
      setTempImage(null);
      toggleLayerSettings('image', slides[selectedSlide].images.length);
    } catch (e) {
      console.error("Use full image failed", e);
    }
  };

  const handleCropSave = async () => {
    try {
      const croppedImage = await getCroppedImg(tempImage, croppedAreaPixels);
      
      const newImage = {
        base64: croppedImage,
        position: { x: 300, y: 600 },
        width: 400,
        height: 400,
        borderRadius: 24,
        start: 0,
        end: slide.duration || 6,
      };

      dispatch(addSlideImage({ slideIndex: selectedSlide, image: newImage }));
      setCropModalOpen(false);
      setTempImage(null);
      toggleLayerSettings('image', slides[selectedSlide].images.length);
    } catch (e) {
      console.error("Crop failed", e);
    }
  };

  const handleMusicSelect = (music) => {
    setMusicError(false);
    dispatch(setTemplateConfig({
      ...templateConfig,
      music_track: music
    }));
  };

  const handleMusicPreview = (music) => {
    if (playingMusicId === music.id) {
        audioRef.current?.pause();
        setPlayingMusicId(null);
    } else {
        audioRef.current?.pause();
        audioRef.current = new Audio(music.uri_full);
        audioRef.current.play();
        setPlayingMusicId(music.id);
        audioRef.current.onended = () => setPlayingMusicId(null);
    }
  };

  const handleAddText = () => {
    const newSlides = JSON.parse(JSON.stringify(slides));

    const defaultFont = templateConfig?.font_track;

    const newText = {
      content: "Add Your Text",
      
      font_family: defaultFont?.family || "serif",

      font_track: {
        ...defaultFont,
        color: defaultFont?.color || "#000000",
        size: defaultFont?.size || 60,
      },

      position: {
        x: 200,
        y: 800,
      },

      animation: {
        type: templateConfig?.global_style?.default_animation?.type || "fade_in_only",
        duration: templateConfig?.global_style?.default_animation?.duration || 1,
      },

      width: 700,
      height: 150,

      start: 0,
      end: slide?.duration || 5,
    };

    newSlides[selectedSlide].texts.push(newText);

    dispatch(setSlides(newSlides));

    const newIndex = newSlides[selectedSlide].texts.length - 1;

    dispatch(setSelectedLayer({
      type: "text",
      index: newIndex
    }));
  };

  useEffect(() => {
    if (generateVideoData?.job_id) {
        if (saveJsonOnly) {
          setGenerationStatus("JSON data saved successfully...");
          setIsGenerating(false);
        } else {
          setGenerationStatus("Initializing Stream...");
          listenToProgress(generateVideoData.job_id);
        }
    }
  }, [generateVideoData]);

  const handleGeneratePreview = () => {
    if (!templateConfig) return;

    if (musicCategories.length > 0 && !templateConfig?.music_track) {
      setMusicError(true);
      setMusicModalOpen(true);
      return;
    }

    setMusicError(false);
    setIsGenerating(true);
    setGenerationStatus("Starting video generation...");
    setGeneratedVideo(null);

    const payload = {
      template_id: templateConfig.id,
      save_json_only: saveJsonOnly ? 1 : 0, // for admin purpose
      payload: {
        ...templateConfig,
        slides,
        ui_base_size_: {
          width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT,
          videoWidth: DESIGN_WIDTH, videoHeight: DESIGN_HEIGHT,
          scale: Number(scale.toFixed(6)),
        },
        ui_base_size: {
          width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT,
          videoWidth: 1080, videoHeight: 1920,
          scale: 1,
        },
        video_size: { w: DESIGN_WIDTH, h: DESIGN_HEIGHT },
      },
    };
      console.log(JSON.stringify(payload))
    dispatch(generateVideoRequest(payload));
  };

  const handleSaveJsonOnlyChange = (checked) => {
    setSaveJsonOnly(checked);
    setIsGenerating(false);
    setGenerationStatus("");
    setGeneratedVideo(null);
  };

  const listenToProgress = (jobId) => {
    const es = new EventSource(
      `${GENERATE_VIDEO_STREAM}/${jobId}?token=${loginData?.token || 'none'}`
    );
    es.onmessage = (e) => {
      const event = JSON.parse(e.data);
      if (event.event === "progress") setGenerationStatus(event.message);
      if (event.event === "completed") {
        setGenerationStatus(event.message);
        setGeneratedVideo(event.data.video);
        setIsGenerating(false);

        // SAVE IN DB
        dispatch(
          updateCreatedInvitationInfoRequest({
            id: event.data.job_id,
            invitation_file: event.data.key,
            storage_driver: event.data.storage_type,
            with_watermark: event.data.with_watermark
          })
        );

        dispatch(generateVideoDataClear());
        es.close();
      }
      if (event.event === "error") {
        setGenerationStatus(`Error: ${event.message}`);
        setIsGenerating(false); 
        es.close();
      }
    };
    es.onerror = () => {
      setGenerationStatus("Connection lost.");
      setIsGenerating(false); 
      es.close();
    };
  };

  // --- LOADING STATE ---
  if (getTemplateByIdLoading ) {
    return (
      <div className="min-h-screen bg-white flex items-center">
        <Loader text="Loading Template Details..." />
      </div>
    );
  }

  // --- NOT FOUND STATE ---
  if (getTemplateByIdError || !slide) {
    return (
      <div className="min-h-screen bg-white flex items-center">
        <NotFound message="Oops! Template not found." />
      </div>
    );
  }

  const isLastSlide = selectedSlide === slides.length - 1;
  
  return (
    <>
      <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row gap-6 lg:gap-10 px-1 sm:px-6 py-2 sm:py-10 items-start overflow-x-hidden">
        
        {/* === PREVIEW AREA === */}
        <div className="w-full lg:w-[420px] flex justify-center">
          <div
            ref={previewContainerRef}
            className="relative bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white mx-auto"
            style={{
              width: "100%",
              // maxWidth: PREVIEW_WIDTH,
              aspectRatio: "9/16",
              // height: previewWidth * (16 / 9), // dynamic height
              // maxHeight: "100vh", // prevent overflow
            }}
            // style={{
            //   width: "100%",
            //   height: previewWidth * (16 / 9),
            //   maxHeight: "80vh",
            //   position: "relative",
            //   flexShrink: 0,
            // }}
          >
            <video
              ref={videoRef}
              src={slide.bg_video_full}
              muted
              loop
              autoPlay
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
            />

            {slide.texts.map((t, i) => (
              <div
                key={i}
                onClick={() => toggleLayerSettings("text", i)}
                style={{
                  position: "absolute",
                  left: t.position.x * scale,
                  top: t.position.y * scale,
                  width: t.width * scale,
                  fontFamily: t.font_track?.family || "serif",
                  fontSize: (t.font_track?.size || 48) * scale,
                  color: t.font_track?.color || "#000",
                  lineHeight: 1.2,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  textAlign: "center",
                  cursor: "pointer",
                  outline:
                    selectedLayer?.type === "text" &&
                    selectedLayer.index === i
                      ? "2px solid #facc15"
                      : "none",
                  ...getTextPreviewStyle(t.text_style),
                }}
                >
                {t.content}
              </div>
            ))}

            {slide.images.map((img, i) => (
              <img
                key={i}
                src={img.base64 || img.uri_full}
                onClick={() => toggleLayerSettings("image", i)}
                style={{
                  position: "absolute",
                  left: img.position.x * scale,
                  top: img.position.y * scale,
                  width: img.width * scale,
                  height: img.height * scale,
                  objectFit: "cover",
                  borderRadius: (img.borderRadius || 0) * scale,
                  cursor: "pointer",
                  outline:
                    selectedLayer?.type === "image" &&
                    selectedLayer.index === i
                      ? "2px solid #facc15"
                      : "none",
                }}
              />
            ))}
          </div>
        </div>

        {/* === RIGHT PANEL === */}
        <div className="flex-1 w-full">
          <h1 className="text-xl sm:text-2xl font-bold mb-6 text-center lg:text-left">
            Customize Your Invitation
          </h1>

          {/* Quick Edit Inputs */}
          <div className="space-y-4">
            {slide.texts.map((t, i) => {
              const isSelected = selectedLayer?.type === "text" && selectedLayer.index === i;
              const itemActive = isSelected ? t : null;
              return (
                <div key={i} className="space-y-2">
                  <div
                    className={`relative rounded-xl transition border-2 ${isSelected ? "border-yellow-400 bg-yellow-50" : "border-gray-200 bg-white"}`}
                  >
                    <input
                      value={t.content}
                      onFocus={() => selectLayerOnly('text', i)}
                      onChange={(e) => handleTextChange(e.target.value)}
                      className="w-full bg-transparent px-4 py-4 pr-14 text-base sm:text-sm outline-none rounded-xl"
                    />
                    {isSuperAdmin && (
                      <div className="absolute right-12 top-1/2 -translate-y-1/2 flex gap-1">
                        {t.text_style?.shadow && (
                          <span className="text-[9px] bg-blue-100 text-blue-600 font-bold px-1.5 py-0.5 rounded-full leading-none">
                            S
                          </span>
                        )}
                        {t.text_style?.backdrop && (
                          <span className="text-[9px] bg-purple-100 text-purple-600 font-bold px-1.5 py-0.5 rounded-full leading-none">
                            B
                          </span>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => toggleLayerSettings('text', i)}
                      className={`absolute right-4 top-1/2 -translate-y-1/2 text-xl transition-transform ${isSelected ? "rotate-90 text-yellow-600" : "text-gray-400"}`}
                    >
                      ⚙️
                    </button>
                  </div>

                  {isSelected && itemActive && (isSuperAdmin || settingsLayerKey === `text-${i}`) && (
                    <div ref={settingsRef} className="bg-white rounded-xl shadow-lg border border-yellow-200 relative z-10 w-full">
                      <div className="bg-yellow-50 px-4 py-3 border-b border-yellow-100 flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-gray-800">Edit Text</h3>
                          <p className="text-sm text-yellow-700 font-medium mt-0.5 truncate max-w-[250px]">{itemActive.content}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={handleCopyTextLayer} className="text-blue-400 hover:text-blue-600 hover:bg-blue-50 p-1.5 rounded-full transition" title="Duplicate Text Layer">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                          <button onClick={handleDeleteLayer} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-full transition" title="Delete Element">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <button onClick={() => { dispatch(setSelectedLayer(null)); setSettingsLayerKey(null); }} className="text-gray-400 hover:text-gray-600 text-xl font-bold px-2">✕</button>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex bg-gray-100 rounded-lg p-1 mb-5">
                          {["style", "position", ...(isSuperAdmin ? ["animation"] : [])].map((tab) => (
                            <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-1.5 rounded-md text-sm font-semibold transition ${activeTab === tab ? "bg-white shadow text-yellow-600" : "text-gray-500 hover:text-gray-700"}`}>
                              {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                          ))}
                        </div>
                        {activeTab === 'style' && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Font Size</label>
                              <p className="text-[10px] text-gray-400 mb-1">Controls how large the text appears in the rendered video.</p>
                              <div className="flex items-center gap-3">
                                <input type="range" min={20} max={120} className="flex-1 accent-yellow-500" value={itemActive.font_track?.size || 48} onChange={(e) => updateLayerProperty("font_track", +e.target.value, "size")} />
                                <span className="text-sm font-mono w-8">{itemActive.font_track?.size}</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Font Family</label>
                              <p className="text-[10px] text-gray-400 mb-1">The typeface applied to this text layer.</p>
                              <div className="relative">
                                <button onClick={() => setShowFontPicker(!showFontPicker)} className="w-full border border-gray-300 p-2.5 rounded-lg flex justify-between items-center bg-white hover:border-yellow-400 transition-colors">
                                  <span style={{ fontFamily: itemActive.font_track?.family, fontSize: '1.1em' }}>
                                    {fontList.find(f => f.family === itemActive.font_track?.family)?.label || itemActive.font_track?.family || "Select Font"}
                                  </span>
                                  <span className="text-xs text-gray-400 rotate-90">›</span>
                                </button>
                                {showFontPicker && (
                                  <div className="absolute z-50 left-0 right-0 top-full mt-2 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-2xl ring-1 ring-black ring-opacity-5">
                                    <div className="p-1 space-y-0.5">
                                      {fontList.map((f) => {
                                        const isActive = itemActive.font_track?.family === f.family;
                                        return (
                                          <div key={f.id} onMouseEnter={() => loadFont(f)} onClick={async () => { await loadFont(f); await handleFontFamilyChange(f.family); setShowFontPicker(false); }} className={`px-4 py-3 cursor-pointer flex items-center justify-between rounded-lg transition-all ${isActive ? "bg-yellow-50 text-yellow-800" : "hover:bg-gray-50 text-gray-700"}`}>
                                            <span style={{ fontFamily: `"${f.family}", ${f.fallback || "serif"}`, fontSize: "18px" }} className="truncate">{f.label}</span>
                                            {isActive && <span className="text-yellow-600 font-bold">✓</span>}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Color</label>
                              <p className="text-[10px] text-gray-400 mb-1">Text fill color shown in the final video output.</p>
                              <div className="flex items-center gap-2">
                                <input type="color" value={itemActive.font_track?.color || "#000000"} className="h-9 w-16 cursor-pointer border border-gray-300 p-0.5 rounded-lg bg-white" onChange={(e) => updateLayerProperty("font_track", e.target.value, "color")} />
                                <span className="text-xs text-gray-500 uppercase font-mono">{itemActive.font_track?.color}</span>
                              </div>
                            </div>

                            <div className="space-y-4">
                              {isSuperAdmin && (
                                <div className="border border-gray-200 rounded-xl overflow-hidden">
                                  
                                  {/* Header */}
                                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                      Text Visibility Style
                                    </p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">
                                      Controls shadow & backdrop in the rendered video
                                    </p>
                                  </div>

                                  <div className="p-3 space-y-4">

                                    {/* SHADOW TOGGLE */}
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-sm font-semibold text-gray-700">Text Shadow</p>
                                        <p className="text-[10px] text-gray-400">Drop shadow behind text for readability</p>
                                      </div>
                                      <button
                                        onClick={() => {
                                          const current = itemActive.text_style?.shadow ?? false;
                                          updateLayerProperty("text_style", !current, "shadow");
                                        }}
                                        className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
                                          itemActive.text_style?.shadow ? "bg-yellow-500" : "bg-gray-300"
                                        }`}
                                      >
                                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                                          itemActive.text_style?.shadow ? "translate-x-5" : "translate-x-0"
                                        }`} />
                                      </button>
                                    </div>

                                    {/* SHADOW INTENSITY — only show when shadow ON */}
                                    {itemActive.text_style?.shadow && (
                                      <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                          Shadow Intensity
                                        </label>
                                        <div className="flex gap-2">
                                          {["soft", "medium", "strong"].map((level) => (
                                            <button
                                              key={level}
                                              onClick={() => updateLayerProperty("text_style", level, "shadow_intensity")}
                                              className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition ${
                                                (itemActive.text_style?.shadow_intensity ?? "strong") === level
                                                  ? "bg-yellow-500 text-white border-yellow-500"
                                                  : "bg-white text-gray-600 border-gray-300 hover:border-yellow-400"
                                              }`}
                                            >
                                              {level.charAt(0).toUpperCase() + level.slice(1)}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* BACKDROP TOGGLE */}
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-sm font-semibold text-gray-700">Text Backdrop</p>
                                        <p className="text-[10px] text-gray-400">Semi-transparent bg behind text</p>
                                      </div>
                                      <button
                                        onClick={() => {
                                          const current = itemActive.text_style?.backdrop ?? false;
                                          updateLayerProperty("text_style", !current, "backdrop");
                                        }}
                                        className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
                                          itemActive.text_style?.backdrop ? "bg-yellow-500" : "bg-gray-300"
                                        }`}
                                      >
                                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                                          itemActive.text_style?.backdrop ? "translate-x-5" : "translate-x-0"
                                        }`} />
                                      </button>
                                    </div>

                                    {/* BACKDROP OPACITY — only show when backdrop ON */}
                                    {itemActive.text_style?.backdrop && (
                                      <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">
                                          Backdrop Opacity
                                        </label>
                                        <div className="flex items-center gap-3">
                                          <input
                                            type="range"
                                            min={0.1}
                                            max={0.85}
                                            step={0.05}
                                            className="flex-1 accent-yellow-500"
                                            value={itemActive.text_style?.backdrop_opacity ?? 0.45}
                                            onChange={(e) =>
                                              updateLayerProperty("text_style", +e.target.value, "backdrop_opacity")
                                            }
                                          />
                                          <span className="text-xs font-mono w-10 text-gray-600">
                                            {Math.round((itemActive.text_style?.backdrop_opacity ?? 0.45) * 100)}%
                                          </span>
                                        </div>
                                      </div>
                                    )}

                                    {/* PREVIEW BADGE */}
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                      {itemActive.text_style?.shadow && (
                                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded-full border border-blue-200">
                                          Shadow: {itemActive.text_style?.shadow_intensity ?? "strong"}
                                        </span>
                                      )}
                                      {itemActive.text_style?.backdrop && (
                                        <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-semibold rounded-full border border-purple-200">
                                          Backdrop: {Math.round((itemActive.text_style?.backdrop_opacity ?? 0.45) * 100)}%
                                        </span>
                                      )}
                                      {!itemActive.text_style?.shadow && !itemActive.text_style?.backdrop && (
                                        <span className="px-2 py-0.5 bg-gray-100 text-gray-400 text-[10px] rounded-full">
                                          No style applied
                                        </span>
                                      )}
                                    </div>

                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {activeTab === 'position' && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">X Position</label>
                              <p className="text-[10px] text-gray-400 mb-1">Horizontal offset from the left edge. 0 = left, 1080 = right.</p>
                              <input type="range" min={0} max={DESIGN_WIDTH} className="w-full accent-yellow-500" value={itemActive.position.x} onChange={(e) => updateLayerProperty("position", +e.target.value, "x")} />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Y Position</label>
                              <p className="text-[10px] text-gray-400 mb-1">Vertical offset from the top. 0 = top, 1920 = bottom.</p>
                              <input type="range" min={0} max={DESIGN_HEIGHT} className="w-full accent-yellow-500" value={itemActive.position.y} onChange={(e) => updateLayerProperty("position", +e.target.value, "y")} />
                            </div>
                          </div>
                        )}
                        {activeTab === 'animation' && isSuperAdmin && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Animation Type</label>
                              <p className="text-[10px] text-gray-400 mb-1">How this element enters the screen when the slide starts playing.</p>
                              <select className="border border-gray-300 bg-white focus:border-purple-400 outline-none p-2 w-full rounded-lg text-xs text-gray-800" value={itemActive.animation?.type || "fade_in_only"} onChange={(e) => { updateLayerProperty("animation", e.target.value, "type"); if (videoRef.current) videoRef.current.pause(); }}>
                                <option value="fade_in_only">Fade In — element gently fades from invisible to visible</option>
                                <option value="fade_in_out">Fade In & Out — fades in at start, fades out at end</option>
                                <option value="slide_up">Slide Up — element slides in from below its final position</option>
                                <option value="slide_down">Slide Down — element slides in from above its final position</option>
                                <option value="slide_in_left">Slide Left — element slides in from the left side</option>
                                <option value="slide_in_right">Slide Right — element slides in from the right side</option>
                                <option value="slide_up_out">Slide Up In & Out — slides in from below, exits upward</option>
                                <option value="slide_down_out">Slide Down In & Out — slides in from above, exits downward</option>
                                <option value="zoom_in">Zoom In — element scales up from small to full size</option>
                                <option value="zoom_out">Zoom Out — element scales down from large to full size</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Animation Duration</label>
                              <p className="text-[10px] text-gray-400 mb-1">How many seconds the entrance animation takes to complete.</p>
                              <div className="flex items-center gap-2">
                                <input type="range" min={0.1} max={3} step={0.1} className="flex-1 accent-purple-500" value={itemActive.animation?.duration ?? 1} onChange={(e) => updateLayerProperty("animation", +e.target.value, "duration")} />
                                <span className="text-xs font-mono w-8 text-gray-700">{itemActive.animation?.duration ?? 1}s</span>
                              </div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                              <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Timeline</label>
                                <p className="text-[10px] text-gray-400 mb-2">Controls when this element is visible within the slide.</p>
                              </div>
                              <div>
                                <label className="text-[11px] text-gray-500 font-medium">Start (sec)</label>
                                <input type="number" step="0.1" min={0} className="border border-gray-300 bg-white focus:border-purple-400 outline-none p-1.5 w-full rounded-lg text-xs text-gray-800 mt-0.5" value={itemActive.start ?? 0} onChange={(e) => updateLayerProperty("start", Number(e.target.value || 0))} />
                              </div>
                              <div>
                                <label className="text-[11px] text-gray-500 font-medium">End (sec)</label>
                                <input type="number" step="0.1" min={0} className="border border-gray-300 bg-white focus:border-purple-400 outline-none p-1.5 w-full rounded-lg text-xs text-gray-800 mt-0.5" value={itemActive.end ?? slides[selectedSlide]?.duration ?? 5} onChange={(e) => updateLayerProperty("end", Number(e.target.value || 0))} />
                              </div>
                              <div className="h-3 bg-gray-200 rounded-full relative overflow-hidden mt-1">
                                <div className="absolute h-3 rounded-full bg-green-400" style={{ left: ((itemActive.start || 0) / (slides[selectedSlide]?.duration || 1)) * 100 + "%", width: (((itemActive.end || 0) - (itemActive.start || 0)) / (slides[selectedSlide]?.duration || 1)) * 100 + "%" }} />
                                <div className="absolute top-0 bottom-0 w-[2px] bg-rose-400" style={{ left: (currentTime / (slides[selectedSlide]?.duration || 1)) * 100 + "%" }} />
                              </div>
                              <div className="flex justify-between text-[10px] text-gray-400 px-0.5">
                                <span>0s</span>
                                <span className="flex items-center gap-2">
                                  <span className="inline-block w-3 h-2 bg-green-400 rounded-sm"></span> visible range
                                  <span className="inline-block w-[2px] h-3 bg-rose-400 rounded-sm"></span> play cursor
                                </span>
                                <span>{slides[selectedSlide]?.duration ?? "?"}s</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Existing image layers */}
            {slide.images.map((img, i) => {
              const isSelected = selectedLayer?.type === "image" && selectedLayer.index === i;
              const itemActive = isSelected ? img : null;
              return (
                <div key={i} className="space-y-2">
                  <div className={`relative rounded-xl transition border-2 flex items-center px-4 py-3 ${isSelected ? "border-yellow-400 bg-yellow-50" : "border-gray-200 bg-white"}`}>
                    <img src={img.base64 || img.uri_full} className="w-10 h-10 object-cover rounded-lg mr-3 shrink-0" alt={`Layer ${i + 1}`} />
                    <span className="flex-1 text-sm font-medium text-gray-700">Image {i + 1}</span>
                    <button onClick={() => toggleLayerSettings('image', i)} className={`text-xl transition-transform ${isSelected ? "rotate-90 text-yellow-600" : "text-gray-400"}`}>⚙️</button>
                  </div>

                  {isSelected && itemActive && (isSuperAdmin || settingsLayerKey === `image-${i}`) && (
                    <div ref={settingsRef} className="bg-white rounded-xl shadow-lg border border-yellow-200 relative z-10 w-full">
                      <div className="bg-yellow-50 px-4 py-3 border-b border-yellow-100 flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-gray-800">Edit Image</h3>
                          <p className="text-sm text-yellow-700 font-medium mt-0.5">Image Layer {i + 1}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={handleDeleteLayer} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-full transition" title="Delete Element">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <button onClick={() => { dispatch(setSelectedLayer(null)); setSettingsLayerKey(null); }} className="text-gray-400 hover:text-gray-600 text-xl font-bold px-2">✕</button>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex bg-gray-100 rounded-lg p-1 mb-5">
                          {["style", "position", ...(isSuperAdmin ? ["animation"] : [])].map((tab) => (
                            <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-1.5 rounded-md text-sm font-semibold transition ${activeTab === tab ? "bg-white shadow text-yellow-600" : "text-gray-500 hover:text-gray-700"}`}>
                              {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                          ))}
                        </div>
                        {activeTab === 'style' && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Image Size</label>
                              <p className="text-[10px] text-gray-400 mb-1">Width of the image on the canvas. Full canvas width is 1080px.</p>
                              <div className="flex items-center gap-3">
                                <input type="range" min={50} max={DESIGN_WIDTH} className="flex-1 accent-yellow-500" value={itemActive.width || 400} onChange={(e) => updateLayerProperty("width", +e.target.value)} />
                                <span className="text-sm font-mono w-10">{itemActive.width}px</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Image Height</label>
                              <p className="text-[10px] text-gray-400 mb-1">Height of the image on the canvas. Full canvas height is 1920px.</p>
                              <div className="flex items-center gap-3">
                                <input type="range" min={50} max={DESIGN_HEIGHT} className="flex-1 accent-yellow-500" value={itemActive.height || 400} onChange={(e) => updateLayerProperty("height", +e.target.value)} />
                                <span className="text-sm font-mono w-10">{itemActive.height}px</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Border Radius</label>
                              <p className="text-[10px] text-gray-400 mb-1">Rounds the image corners. Set to half the width to make it a circle.</p>
                              <div className="flex items-center gap-3">
                                <input type="range" min={0} max={Math.floor(Math.min(itemActive.width || 0, itemActive.height || 0) / 2)} value={itemActive.borderRadius || 0} className="flex-1 accent-yellow-500" onChange={(e) => updateLayerProperty("borderRadius", +e.target.value)} />
                                <span className="text-sm font-mono w-10">{itemActive.borderRadius || 0}px</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {activeTab === 'position' && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">X Position</label>
                              <p className="text-[10px] text-gray-400 mb-1">Horizontal offset from the left edge. 0 = left, 1080 = right.</p>
                              <input type="range" min={0} max={DESIGN_WIDTH} className="w-full accent-yellow-500" value={itemActive.position.x} onChange={(e) => updateLayerProperty("position", +e.target.value, "x")} />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Y Position</label>
                              <p className="text-[10px] text-gray-400 mb-1">Vertical offset from the top. 0 = top, 1920 = bottom.</p>
                              <input type="range" min={0} max={DESIGN_HEIGHT} className="w-full accent-yellow-500" value={itemActive.position.y} onChange={(e) => updateLayerProperty("position", +e.target.value, "y")} />
                            </div>
                          </div>
                        )}
                        {activeTab === 'animation' && isSuperAdmin && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Animation Type</label>
                              <p className="text-[10px] text-gray-400 mb-1">How this element enters the screen when the slide starts playing.</p>
                              <select className="border border-gray-300 bg-white focus:border-purple-400 outline-none p-2 w-full rounded-lg text-xs text-gray-800" value={itemActive.animation?.type || "fade_in_only"} onChange={(e) => { const upd = JSON.parse(JSON.stringify(slides)); upd[selectedSlide].images[selectedLayer.index].animation = upd[selectedSlide].images[selectedLayer.index].animation || {}; upd[selectedSlide].images[selectedLayer.index].animation.type = e.target.value; dispatch(setSlides(upd)); if (videoRef.current) videoRef.current.pause(); }}>
                                <option value="fade_in_only">Fade In — element gently fades from invisible to visible</option>
                                <option value="fade_in_out">Fade In & Out — fades in at start, fades out at end</option>
                                <option value="slide_up">Slide Up — element slides in from below its final position</option>
                                <option value="slide_down">Slide Down — element slides in from above its final position</option>
                                <option value="slide_in_left">Slide Left — element slides in from the left side</option>
                                <option value="slide_in_right">Slide Right — element slides in from the right side</option>
                                <option value="slide_up_out">Slide Up In & Out — slides in from below, exits upward</option>
                                <option value="slide_down_out">Slide Down In & Out — slides in from above, exits downward</option>
                                <option value="zoom_in">Zoom In — element scales up from small to full size</option>
                                <option value="zoom_out">Zoom Out — element scales down from large to full size</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Animation Duration</label>
                              <p className="text-[10px] text-gray-400 mb-1">How many seconds the entrance animation takes to complete.</p>
                              <div className="flex items-center gap-2">
                                <input type="range" min={0.1} max={3} step={0.1} className="flex-1 accent-purple-500" value={itemActive.animation?.duration ?? 1} onChange={(e) => { const upd = JSON.parse(JSON.stringify(slides)); upd[selectedSlide].images[selectedLayer.index].animation = upd[selectedSlide].images[selectedLayer.index].animation || {}; upd[selectedSlide].images[selectedLayer.index].animation.duration = +e.target.value; dispatch(setSlides(upd)); }} />
                                <span className="text-xs font-mono w-8 text-gray-700">{itemActive.animation?.duration ?? 1}s</span>
                              </div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                              <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-0.5">Timeline</label>
                                <p className="text-[10px] text-gray-400 mb-2">Controls when this element is visible within the slide.</p>
                              </div>
                              <div>
                                <label className="text-[11px] text-gray-500 font-medium">Start (sec)</label>
                                <input type="number" step="0.1" min={0} className="border border-gray-300 bg-white focus:border-purple-400 outline-none p-1.5 w-full rounded-lg text-xs text-gray-800 mt-0.5" value={itemActive.start ?? 0} onChange={(e) => updateLayerProperty("start", Number(e.target.value || 0))} />
                              </div>
                              <div>
                                <label className="text-[11px] text-gray-500 font-medium">End (sec)</label>
                                <input type="number" step="0.1" min={0} className="border border-gray-300 bg-white focus:border-purple-400 outline-none p-1.5 w-full rounded-lg text-xs text-gray-800 mt-0.5" value={itemActive.end ?? slides[selectedSlide]?.duration ?? 5} onChange={(e) => updateLayerProperty("end", Number(e.target.value || 0))} />
                              </div>
                              <div className="h-3 bg-gray-200 rounded-full relative overflow-hidden mt-1">
                                <div className="absolute h-3 rounded-full bg-green-400" style={{ left: ((itemActive.start || 0) / (slides[selectedSlide]?.duration || 1)) * 100 + "%", width: (((itemActive.end || 0) - (itemActive.start || 0)) / (slides[selectedSlide]?.duration || 1)) * 100 + "%" }} />
                                <div className="absolute top-0 bottom-0 w-[2px] bg-rose-400" style={{ left: (currentTime / (slides[selectedSlide]?.duration || 1)) * 100 + "%" }} />
                              </div>
                              <div className="flex justify-between text-[10px] text-gray-400 px-0.5">
                                <span>0s</span>
                                <span className="flex items-center gap-2">
                                  <span className="inline-block w-3 h-2 bg-green-400 rounded-sm"></span> visible range
                                  <span className="inline-block w-[2px] h-3 bg-rose-400 rounded-sm"></span> play cursor
                                </span>
                                <span>{slides[selectedSlide]?.duration ?? "?"}s</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* === ADD TEXT + ADD IMAGE + MUSIC BUTTONS === */}
            <input type="file" accept="image/*" ref={fileInputRef} onChange={onFileChange} className="hidden" />
            <div className={`grid gap-3 pt-2 ${user ? "grid-cols-2" : "grid-cols-1"}`}>
              {user && (
                <button onClick={handleAddText} className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-100 rounded-xl hover:bg-purple-100 transition group">
                  <div className="w-10 h-10 bg-white border border-purple-200 rounded-lg flex items-center justify-center relative shrink-0 shadow-sm">
                    <span className="text-purple-500 font-bold text-lg">T</span>
                    <div className="absolute -bottom-1.5 -right-1.5 bg-green-500 rounded-full w-4 h-4 flex items-center justify-center border border-white">
                      <span className="text-white font-bold text-[10px] leading-none">+</span>
                    </div>
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-sm font-semibold text-gray-700 group-hover:text-purple-600">Add Text</p>
                    <p className="text-[11px] text-gray-400">New text layer</p>
                  </div>
                </button>
              )}

              <button onClick={triggerImageUpload} className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-xl hover:bg-blue-100 transition group">
                <div className="w-10 h-10 bg-white border border-blue-200 rounded-lg flex items-center justify-center relative shrink-0 shadow-sm">
                  <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                  <div className="absolute -bottom-1.5 -right-1.5 bg-green-500 rounded-full w-4 h-4 flex items-center justify-center border border-white">
                    <span className="text-white font-bold text-[10px] leading-none">+</span>
                  </div>
                </div>
                <div className="text-left min-w-0">
                  <p className="text-sm font-semibold text-gray-700 group-hover:text-blue-600">Add Image</p>
                  <p className="text-[11px] text-gray-400">Upload & crop</p>
                </div>
              </button>
            </div>

            {/* === MUSIC BUTTON === */}
            {isLastSlide && musicCategories.length > 0 && (
              <button
                onClick={() => setMusicModalOpen(true)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition ${
                  musicError
                    ? "bg-red-50 border-red-300"
                    : templateConfig?.music_track
                    ? "bg-green-50 border-green-200 hover:bg-green-100"
                    : "bg-yellow-50 border-yellow-200 hover:bg-yellow-100"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🎵</span>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-700">Choose Music</p>
                    <p className="text-[11px] text-gray-400 truncate max-w-[180px]">
                      {templateConfig?.music_track ? templateConfig.music_track.title : "No music selected"}
                    </p>
                  </div>
                </div>
                {templateConfig?.music_track ? (
                  <span className="text-xs text-green-700 font-semibold bg-green-100 px-2 py-1 rounded-full shrink-0">✓ Selected</span>
                ) : (
                  <span className="text-xs text-red-500 font-semibold bg-red-50 border border-red-200 px-2 py-1 rounded-full shrink-0">Required</span>
                )}
              </button>
            )}
          </div>

          {/* Generate Button */}
          {isLastSlide && (
              <div className="space-y-4">
                {generationStatus && (
                  <div className="p-3 mt-3 bg-blue-50 text-blue-800 text-sm font-medium rounded-lg border border-blue-100 flex items-center gap-2">
                    <span className="animate-pulse">●</span> {generationStatus}
                  </div>
                )}
                
                {isSuperAdmin && (
                  <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="saveJsonOnly"
                      checked={saveJsonOnly}
                      onChange={(e) => handleSaveJsonOnlyChange(e.target.checked)}
                      className="w-4 h-4 accent-purple-600 cursor-pointer"
                    />
                    <label htmlFor="saveJsonOnly" className="text-sm font-medium text-purple-800 cursor-pointer">
                      Save JSON Only (Do not generate video)
                    </label>
                  </div>
                )}

                {musicError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm font-medium rounded-xl">
                    <svg className="w-4 h-4 shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Please select a music track before generating the preview.
                  </div>
                )}

                <button
                  onClick={handleGeneratePreview}
                  disabled={isGenerating}
                  className={`
                    w-full relative overflow-hidden py-4 sm:py-5 rounded-2xl font-bold text-base sm:text-lg
                    transition-all duration-200 active:scale-95
                    disabled:opacity-70 disabled:cursor-not-allowed
                    ${musicError
                      ? "bg-gradient-to-r from-red-400 to-orange-400 text-white shadow-md animate-pulse"
                      : "bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white shadow-lg hover:shadow-orange-300/60 hover:shadow-xl hover:brightness-110"
                    }
                  `}
                >
                  <span className="flex items-center justify-center gap-2">
                    {isGenerating ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Generating Video...
                      </>
                    ) : musicError ? (
                      <>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                        </svg>
                        Select Music First
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 4a1 1 0 011-1h1.5a1 1 0 01.8.4L7 5h10a1 1 0 01.95 1.316l-2.5 7A1 1 0 0114.5 14H3a1 1 0 01-1-1V4z" />
                          <path d="M14 6l1.5-2h1a1 1 0 011 1v8a1 1 0 01-1 1h-1l-1.5-2H6V6h8z" />
                        </svg>
                        Generate Preview
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      </>
                    )}
                  </span>
                </button>

               {generatedVideo && (
                  <div className="mt-6 border-2 border-green-400 rounded-xl overflow-hidden bg-green-50">

                    {/* Video Preview */}
                    <div className="flex justify-center bg-black">
                      <div
                        className="w-full max-w-[280px] sm:max-w-[320px]"
                        style={{ aspectRatio: "9 / 16" }}
                      >
                        <video
                          src={generatedVideo}
                          controls
                          playsInline
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    {/* Success Message */}
                    <div className="p-4 text-center">
                      
                      <div className="flex justify-center mb-2">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center border-2 border-green-300">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>

                      <h4 className="font-bold text-green-800 text-base mb-1">
                        Invitation Video Generated Successfully!
                      </h4>
                      <p className="text-xs text-green-700 mb-4">
                        Your video is saved. Download it anytime from the Download Manager.
                      </p>

                      {/* Go to Manager Button */}
                      
                        <a href="/generated-invitations"
                        className="block w-full text-center bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-bold text-sm transition shadow-md"
                      >
                        📂 Go to Download Manager
                      </a>

                    </div>
                  </div>
                )}

              </div>
          )}

          {/* Navigation */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:gap-0 justify-between items-center pt-6 border-t border-gray-200">
             <button disabled={selectedSlide === 0} onClick={() => dispatch(setSelectedSlide(selectedSlide - 1))} className="px-6 py-2 rounded-full bg-gray-100 text-gray-600 font-medium hover:bg-gray-200 disabled:opacity-50 transition">◀ Previous</button>
             <span className="py-1 px-3 bg-gray-100 rounded text-sm text-gray-500 font-mono">Page {selectedSlide + 1} of {slides.length}</span>
             <button disabled={isLastSlide} onClick={() => dispatch(setSelectedSlide(selectedSlide + 1))} className="px-6 py-2 rounded-full bg-gray-900 text-white font-medium hover:bg-black disabled:opacity-50 transition">Next ▶</button>
          </div>

        </div>
      </div>

      {/* === MUSIC MODAL === */}
      {musicModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg overflow-hidden shadow-2xl flex flex-col max-h-[85vh]">

            {/* Header */}
            <div className="px-4 py-4 border-b flex justify-between items-center shrink-0">
              <div>
                <h3 className="font-bold text-gray-800 text-lg">🎵 Choose Music</h3>
                {templateConfig?.music_track && (
                  <p className="text-xs text-green-600 font-medium mt-0.5 truncate max-w-[280px]">✓ {templateConfig.music_track.title}</p>
                )}
              </div>
              <button onClick={() => setMusicModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition">✕</button>
            </div>

            {/* Category tabs */}
            <div className="flex overflow-x-auto gap-1 p-2 border-b border-gray-100 scrollbar-hide shrink-0">
              {musicCategories.map((cat) => {
                const isActive = selectedMusicCategory === cat.category_id;
                return (
                  <button
                    key={cat.category_id}
                    onClick={() => setSelectedMusicCategory(cat.category_id)}
                    className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                      isActive ? "bg-yellow-500 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {cat.category_name}
                    <span className={`ml-1.5 text-[10px] ${isActive ? "opacity-80" : "text-gray-400"}`}>({cat.songs.length})</span>
                  </button>
                );
              })}
            </div>

            {/* Songs list */}
            <div className="overflow-y-auto flex-1 p-2 space-y-1">
              {(musicCategories.find(c => c.category_id === selectedMusicCategory)?.songs || []).map((m) => {
                const isSelected = templateConfig?.music_track?.id === m.id;
                return (
                  <div
                    key={m.id}
                    className={`flex justify-between items-center p-3 rounded-lg transition-all border ${
                      isSelected ? "bg-yellow-50 border-yellow-300 shadow-sm" : "bg-white border-transparent hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs shrink-0 transition-colors ${isSelected ? "bg-yellow-500 text-white" : "bg-gray-100 text-gray-500"}`}>
                        {isSelected ? "✔" : "♫"}
                      </div>
                      <span className={`text-sm font-medium truncate ${isSelected ? "text-yellow-800" : "text-gray-700"}`}>{m.title}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        className={`w-8 h-8 flex items-center justify-center rounded-full transition ${playingMusicId === m.id ? "bg-yellow-100 text-yellow-600 animate-pulse" : "hover:bg-gray-200 text-gray-600"}`}
                        title={playingMusicId === m.id ? "Pause" : "Preview"}
                        onClick={() => handleMusicPreview(m)}
                      >
                        {playingMusicId === m.id ? "⏸" : "▶"}
                      </button>
                      <button
                        onClick={() => handleMusicSelect(m)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                          isSelected ? "bg-yellow-500 text-white shadow-sm cursor-default" : "bg-gray-100 text-gray-700 hover:bg-gray-800 hover:text-white"
                        }`}
                      >
                        {isSelected ? "Selected" : "Select"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Done */}
            <div className="p-4 border-t bg-gray-50 shrink-0">
              <button
                onClick={() => { setMusicModalOpen(false); setMusicError(false); }}
                className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded-xl transition"
              >
                Done
              </button>
            </div>

          </div>
        </div>
      )}

      {/* === CROP MODAL OVERLAY === */}
      {cropModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl overflow-hidden shadow-2xl">

            {/* Header */}
            <div className="px-4 py-3 border-b flex justify-between items-center bg-gray-50">
              <h3 className="font-bold text-gray-800">Crop Image</h3>
              <button onClick={() => setCropModalOpen(false)} className="text-gray-500 hover:text-gray-700 text-xl leading-none">✕</button>
            </div>

            {/* Crop canvas */}
            <div className="relative bg-gray-900 w-full" style={{ height: "55vh" }}>
              <Cropper
                image={tempImage}
                crop={crop}
                zoom={zoom}
                aspect={cropAspect ?? undefined}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                style={{ containerStyle: { borderRadius: 0 } }}
              />
            </div>

            {/* Controls */}
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-10 shrink-0">Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={5}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <span className="text-xs font-mono text-gray-600 w-10 text-right">{Number(zoom).toFixed(2)}×</span>
              </div>

              <div className="flex justify-between items-center pt-1 gap-3 flex-wrap">
                <button
                  onClick={handleUseFullImage}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition"
                  title="Skip cropping and use the entire original image"
                >
                  Use Full Image
                </button>
                <div className="flex gap-3 ml-auto">
                  <button
                    onClick={() => setCropModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropSave}
                    className="px-6 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-md transition"
                  >
                    Crop & Add
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

    </>
  );
}
